import { supabase } from '@/integrations/supabase/client';
import type { Holiday, HolidayScope } from '@/types/deadlines';

/**
 * Obtiene los festivos para un año y ámbito determinado
 */
export async function getHolidays(
  year: number,
  scopes: HolidayScope[] = ['nacional'],
  organizationId?: string
): Promise<Holiday[]> {
  let query = supabase
    .from('holidays')
    .select('*')
    .eq('year', year)
    .in('scope', scopes);

  // Si hay organización, incluir también festivos locales
  if (organizationId) {
    query = supabase
      .from('holidays')
      .select('*')
      .eq('year', year)
      .or(`scope.in.(${scopes.join(',')}),and(scope.eq.local,organization_id.eq.${organizationId})`);
  }

  const { data, error } = await query;

  if (error) {
    console.error('Error fetching holidays:', error);
    return [];
  }

  return (data || []) as Holiday[];
}

/**
 * Obtiene los festivos para un rango de fechas
 */
export async function getHolidaysInRange(
  startDate: Date,
  endDate: Date,
  scopes: HolidayScope[] = ['nacional'],
  organizationId?: string
): Promise<Holiday[]> {
  const startYear = startDate.getFullYear();
  const endYear = endDate.getFullYear();
  
  const years = [];
  for (let year = startYear; year <= endYear; year++) {
    years.push(year);
  }

  const allHolidays: Holiday[] = [];
  
  for (const year of years) {
    const holidays = await getHolidays(year, scopes, organizationId);
    allHolidays.push(...holidays);
  }

  // Filtrar solo los que están en el rango
  return allHolidays.filter(h => {
    const holidayDate = new Date(h.date);
    return holidayDate >= startDate && holidayDate <= endDate;
  });
}

/**
 * Verifica si una fecha es festivo
 */
export function isHoliday(
  date: Date,
  holidays: Holiday[]
): boolean {
  const dateStr = date.toISOString().split('T')[0];
  return holidays.some(h => h.date === dateStr);
}

/**
 * Verifica si una fecha es fin de semana
 */
export function isWeekend(date: Date): boolean {
  const day = date.getDay();
  return day === 0 || day === 6; // 0 = Domingo, 6 = Sábado
}

/**
 * Verifica si una fecha es día hábil
 */
export function isBusinessDay(date: Date, holidays: Holiday[]): boolean {
  return !isWeekend(date) && !isHoliday(date, holidays);
}

/**
 * Crea un festivo local para una organización
 */
export async function createLocalHoliday(
  date: Date,
  name: string,
  organizationId: string
): Promise<Holiday | null> {
  const { data, error } = await supabase
    .from('holidays')
    .insert({
      date: date.toISOString().split('T')[0],
      name,
      scope: 'local' as HolidayScope,
      organization_id: organizationId,
      year: date.getFullYear(),
    })
    .select()
    .single();

  if (error) {
    console.error('Error creating local holiday:', error);
    return null;
  }

  return data as Holiday;
}

/**
 * Elimina un festivo local
 */
export async function deleteLocalHoliday(holidayId: string): Promise<boolean> {
  const { error } = await supabase
    .from('holidays')
    .delete()
    .eq('id', holidayId)
    .eq('scope', 'local');

  if (error) {
    console.error('Error deleting local holiday:', error);
    return false;
  }

  return true;
}

/**
 * Obtiene los ámbitos de festivos según la CCAA del usuario
 */
export function getHolidayScopesForCCAA(autonomousCommunity: string): HolidayScope[] {
  const scopes: HolidayScope[] = ['nacional'];
  
  if (autonomousCommunity === 'andalucia') {
    scopes.push('andalucia');
  } else if (autonomousCommunity === 'extremadura') {
    scopes.push('extremadura');
  }
  
  return scopes;
}
